/**
 * Who may do what, to an agent or to a whole organization.
 *
 * A permission group is a role granted to a set of users over one entity, so the listing is read
 * per entity type and the same query serves both.
 */
import { z } from 'zod';
import { FlowApiPageLimitSchema, FlowApiPageOffsetSchema } from '../pagination/pageLimit.js';
/** What a role is granted over. */
export var FlowPermissionEntityType;
(function (FlowPermissionEntityType) {
    FlowPermissionEntityType["Agent"] = "Agent";
    FlowPermissionEntityType["Organization"] = "Organization";
})(FlowPermissionEntityType || (FlowPermissionEntityType = {}));
/** The permissions a role carries, as the authorization service names them. */
export var FlowRebacPermission;
(function (FlowRebacPermission) {
    FlowRebacPermission["AgentView"] = "flowbuilder.agent.view";
    FlowRebacPermission["AgentModify"] = "flowbuilder.agent.modify";
    FlowRebacPermission["AgentPublish"] = "flowbuilder.agent.publish";
    FlowRebacPermission["AgentGrant"] = "flowbuilder.agent.grant";
})(FlowRebacPermission || (FlowRebacPermission = {}));
/**
 * Query parameters `GET /permissions` accepts.
 *
 * Both the singular and plural spellings are taken: the singular pair came first and callers have
 * not all moved, so a request naming one entity means the same as a request naming a list of one.
 */
export const FlowPermissionListQuerySchema = z.object({
    entityType: z.enum(FlowPermissionEntityType).describe('Whether to list agents or organizations.'),
    entityIds: z.array(z.uuid()).describe('Return only these entities.').optional(),
    entityId: z.uuid().describe('Return only this entity.').optional(),
    organizationIds: z.array(z.uuid()).describe('Return only these organizations.').optional(),
    organizationId: z.uuid().describe('Return only this organization.').optional(),
    limit: FlowApiPageLimitSchema.describe('How many groups to return.').optional(),
    offset: FlowApiPageOffsetSchema.describe('How many matches to skip, for paging.').optional(),
});
/**
 * Body `POST /permissions/bulk` accepts: every user's role over one entity, set in one request.
 * A null role removes the user from the entity.
 */
export const FlowPermissionBulkUpsertBodySchema = z.object({
    entityType: z
        .enum(FlowPermissionEntityType)
        .describe('Whether the entity is an agent or an organization.'),
    entityId: z.uuid().describe('The entity.'),
    userRoles: z
        .record(z.uuid(), z.uuid().nullable())
        .describe('Role each user is to hold, by user id, or null to remove them.'),
});
//# sourceMappingURL=permission.js.map